import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';

import { styled } from '@spazious/dollhouse-theme';

import { pickProps } from '../../utils/index.js';
import { Field, FieldProps, FieldPropsList } from '../Field/index.js';
import {
  Select,
  SelectElement,
  SelectOptionProps,
  SelectProps,
  SelectPropsList,
} from '../Select/index.js';

export type SelectFieldElement = SelectElement;
export interface SelectFieldProps extends Omit<OverrideProps<FieldProps, SelectProps>, 'children'> {
  label: string;
  inputRef?: React.Ref<HTMLSelectElement>;
  options: Array<Omit<SelectOptionProps, 'ref'>>;
}

export const SelectField = styled(
  forwardRef<HTMLDivElement, SelectFieldProps>(
    ({ label, options, inputRef: inputRefProp, ...restProps }, ref) => {
      const inputRef = useRef<HTMLSelectElement>(null);

      useImperativeHandle(inputRefProp, () => inputRef.current!, []);

      const [fieldProps] = pickProps<FieldProps>(
        restProps,
        FieldPropsList.filter((p) => !SelectPropsList.includes(p as any))
      );
      const [selectProps] = pickProps<SelectProps>(restProps, SelectPropsList);

      return (
        <Field ref={ref} {...fieldProps}>
          <Field.Label>{label}</Field.Label>
          <Select ref={(node) => node} {...selectProps}>
            {useMemo(
              () =>
                options.map(({ value, ...restOptProps }, index) => (
                  <Select.Option
                    key={(value ?? index).toLocaleString()}
                    value={value}
                    {...restOptProps}
                  />
                )),
              [options]
            )}
          </Select>
          <Field.HelperText />
        </Field>
      );
    }
  )
).withConfig({
  displayName: 'SelectField',
})``;
